import { useState, useEffect } from 'react'
import type { ComponentType, ComponentConfig } from '../types/builder.types'
import { executeComponentQuery } from '../utils/query-builder'
import {
  getCustomQueryConfigSignature,
  normalizeQueryResultData,
} from '../utils/custom-query-builder-fields'
import { getBmpLayerUrl, getBmpLayerIdFromBaseTable, isBmpBaseTable } from '../lib/bmp-layers'
import { supabase } from '../lib/supabase'
import { KPICard } from './visualizations/KPICard'
import { TimeSeriesChart } from './visualizations/TimeSeriesChart'
import { BarChartComponent } from './visualizations/BarChartComponent'
import { DataTable } from './visualizations/DataTable'
// Fase 1: Componentes críticos
import { MapComponent } from './visualizations/MapComponent'
import { PieChartComponent } from './visualizations/PieChartComponent'
import { GaugeComponent } from './visualizations/GaugeComponent'
// Fase 2: Componentes muy útiles
import { AreaChartComponent } from './visualizations/AreaChartComponent'
import { StackedBarComponent } from './visualizations/StackedBarComponent'
import { ScatterPlotComponent } from './visualizations/ScatterPlotComponent'
// Fase 3: Nice to have
import { HeatmapComponent } from './visualizations/HeatmapComponent'
import { TreemapComponent } from './visualizations/TreemapComponent'
import { RadarChartComponent } from './visualizations/RadarChartComponent'
import { RadialBarComponent } from './visualizations/RadialBarComponent'
// CKAN: Datos externos
import { ForestAssociationsTable } from './visualizations/ForestAssociationsTable'

interface ComponentRendererProps {
  component: {
    id: any
    type: ComponentType
    configuration: ComponentConfig
    name: string
    filterMunicipio?: string
    filterEspecie?: number
  }
  filterMunicipio?: any
  filterEspecie?: number
  filterMunicipioNombre?: any
}

export function ComponentRenderer({ 
  component, 
  filterMunicipio, 
  filterEspecie, 
  filterMunicipioNombre 
}: ComponentRendererProps) {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)  

  const config = {
    ...component.configuration,
    filterMunicipio,
    filterEspecie
  }

  const customQuerySignature = getCustomQueryConfigSignature(config.customQuery)

  // Función para construir el título con el nombre del municipio si existe
  const getTitleWithMunicipio = () => {
    const baseTitle = config.metadata?.title || component.name;
    
    if (filterMunicipioNombre) {
      return `${baseTitle} - ${filterMunicipioNombre}`;
    }
    
    return baseTitle;
  };

  useEffect(() => {
    loadComponentData()
  }, [component.id, filterMunicipio, filterEspecie, customQuerySignature])

  const loadComponentData = async () => {
    try {
      setLoading(true)
      setError(null)
      const baseTable = config.customQuery?.config?.baseTable
      const isMapBmp =
        component.type === 'map' &&
        config.dataSource === 'custom_query' &&
        config.customQuery?.enabled &&
        baseTable &&
        isBmpBaseTable(baseTable)

      if (isMapBmp) {
        const layerId = getBmpLayerIdFromBaseTable(baseTable)
        const url = layerId ? getBmpLayerUrl(layerId) : null
        if (!url) {
          setData(null)
          setLoading(false)
          return
        }
        const res = await fetch(url)
        const geojson = await res.json().catch(() => null)
        if (!geojson?.features) {
          setData(null)
          setLoading(false)
          return
        }
        let features = geojson.features
        if (filterMunicipio) {
          const { data: muni } = await supabase
            .from('municipios')
            .select('codigo_ine')
            .eq('id_municipio', filterMunicipio)
            .maybeSingle()
          const muniRow = muni as { codigo_ine?: string } | null
          const codigoIne = muniRow?.codigo_ine != null ? String(muniRow.codigo_ine).trim() : null
          if (codigoIne) {
            features = features.filter((f: { properties?: Record<string, unknown> }) => {
              const cod = f.properties?.CODMUN ?? f.properties?.c_mun
              return cod != null && String(cod).trim() === codigoIne
            })
          }
        }
        setData({ ...geojson, features })
      } else {
        const result = await executeComponentQuery(config)
        setData(
          normalizeQueryResultData(result, component.type) as
            | number
            | Record<string, unknown>[]
        )
      }
    } catch (err) {
      console.error('Error cargando datos del componente:', err)
      setError('Error al cargar los datos')
    } finally {
      setLoading(false)
    }
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-lg p-6">
        <div className="text-center">
          <p className="text-red-600 mb-3">{error}</p>
          <button
            onClick={loadComponentData}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  // Obtener el título con el nombre del municipio si aplica
  const componentTitle = getTitleWithMunicipio();

  // Render component based on type
  switch (component.type) {
    case 'kpi':
      return (
        <KPICard
          title={componentTitle}
          value={data || 0}
          unit={config.styling?.unit}
          color={config.styling?.color}
          icon={config.styling?.icon}
        />
      )

    case 'line-chart':
      return (
        <TimeSeriesChart
          title={componentTitle}
          data={data || []}
          dataKey={config.chartConfig?.yAxisKey || 'valor'}
          xAxisKey={config.chartConfig?.xAxisKey || 'fecha'}
          color={config.chartConfig?.chartColor || '#10b981'}
        />
      )

    case 'bar-chart': {
      // Alias del QueryBuilder (p. ej. biomasa_total) antes de resolveRowKeyForDataKey:
      // claves largas con "+" hacen que split('.').pop() sea biomasa_raiz en vez del total.
      const barAliases = config.customQuery?.config?.fieldAliases as
        | Record<string, string>
        | undefined
      const resolveBarKey = (k: string) => (barAliases && barAliases[k]) || k
      return (
        <BarChartComponent
          title={componentTitle}
          data={data || []}
          xKey={resolveBarKey(config.chartConfig?.xAxisKey || 'categoria')}
          yKey={resolveBarKey(config.chartConfig?.yAxisKey || 'valor')}
          color={config.chartConfig?.chartColor || '#3b82f6'}
        />
      )
    }

    case 'table':
      // Transform tableConfig columns to DataTable Column format.
      // Mantener col.key tal cual para que row[col.key] coincida con las claves de los datos
      // (p. ej. "municipios.nombre", "municipios.codigo_ine" en consultas con join).
      const tableColumns = (config.tableConfig?.columns || []).map((col) => {
        const raw = col.label ?? col.key ?? ''
        const labelText = typeof raw === 'string' ? raw : String(raw)
        return {
          key: col.key,
          label: labelText.includes('.')
            ? labelText.split('.').pop()!.replace(/_/g, ' ')
            : labelText.replace(/_/g, ' '),
          sortable: col.sortable,
        }
      })

      return (
        <DataTable
          title={componentTitle}
          data={data || []}
          columns={tableColumns}
          pageSize={config.tableConfig?.pageSize || 10}
          loading={loading}
        />
      )

    // =====================================================
    // FASE 1: COMPONENTES CRÍTICOS
    // =====================================================

    case 'map': {
      const baseTable = config.customQuery?.config?.baseTable
      const isMapBmp = config.dataSource === 'custom_query' && baseTable && isBmpBaseTable(baseTable)
      const geojsonData = isMapBmp && data && data.features
        ? data
        : (data && Array.isArray(data) && data.length > 0 ? data[0] : null)
      const displayFields = isMapBmp ? (config.customQuery?.config?.selectedFields || []) : undefined
      return (
        <MapComponent
          title={config.metadata?.title || component.name}
          mapType={config.mapConfig?.mapType}
          geojsonData={geojsonData}
          displayFields={displayFields}
          centerLat={config.mapConfig?.centerLat}
          centerLon={config.mapConfig?.centerLon}
          zoom={config.mapConfig?.zoom}
        />
      )
    }

    case 'pie-chart': {
      const pieAliases = config.customQuery?.config?.fieldAliases as Record<string, string> | undefined
      const resolvePieKey = (k: string) => (pieAliases && pieAliases[k]) || k
      let dataKey = resolvePieKey(config.pieConfig?.dataKey || 'value')
      let nameKey = resolvePieKey(config.pieConfig?.nameKey || 'name')
      const pieRows = (data || []) as Record<string, unknown>[]
      const pieFirst = pieRows[0]
      if (pieFirst) {
        const missingValue =
          pieFirst[dataKey] === undefined || pieFirst[dataKey] === null
        if (missingValue && pieAliases) {
          for (const aliasCol of Object.values(pieAliases)) {
            if (pieFirst[aliasCol] != null && !Number.isNaN(Number(pieFirst[aliasCol]))) {
              dataKey = aliasCol
              break
            }
          }
        }
        if (pieFirst[nameKey] === undefined || pieFirst[nameKey] === null) {
          const catKeys = Object.keys(pieFirst).filter(
            (k) => k !== dataKey && pieFirst[k] != null && Number.isNaN(Number(pieFirst[k]))
          )
          if (catKeys.length === 1) nameKey = catKeys[0]
        }
      }
      return (
        <PieChartComponent
          title={componentTitle}
          data={data || []}
          dataKey={dataKey}
          nameKey={nameKey}
          showPercentage={config.pieConfig?.showPercentage}
          innerRadius={config.pieConfig?.innerRadius}
          colors={config.pieConfig?.colors}
        />
      )
    }

    case 'gauge':
      return (
        <GaugeComponent
          title={componentTitle}
          data={data}
          valueKey={config.gaugeConfig?.valueKey || 'value'}
          min={config.gaugeConfig?.min}
          max={config.gaugeConfig?.max}
          unit={config.gaugeConfig?.unit}
          thresholds={config.gaugeConfig?.thresholds}
        />
      )

    // =====================================================
    // FASE 2: COMPONENTES MUY ÚTILES
    // =====================================================

    case 'area-chart':
      return (
        <AreaChartComponent
          title={componentTitle}
          data={data || []}
          xAxisKey={config.areaChartConfig?.xAxisKey || 'fecha'}
          yAxisKeys={config.areaChartConfig?.yAxisKeys || ['valor']}
          stacked={config.areaChartConfig?.stacked}
          areaType={config.areaChartConfig?.areaType}
          showGrid={config.areaChartConfig?.showGrid}
          colors={config.areaChartConfig?.colors}
        />
      )

    case 'stacked-bar': {
      const fieldAliases = config.customQuery?.config?.fieldAliases as Record<string, string> | undefined
      const resolveKey = (k: string) => (fieldAliases && fieldAliases[k]) || k

      const xAxisKeyConfig = config.stackedBarConfig?.xAxisKey || 'categoria'
      const xAxisKey = resolveKey(xAxisKeyConfig)
      const stackCategoryKey = config.stackedBarConfig?.stackCategoryKey
      const valueKey = config.stackedBarConfig?.valueKey
        ? resolveKey(config.stackedBarConfig.valueKey)
        : undefined
      const rawData = data || []
      let chartData = rawData
      const yAxisKeysConfig = config.stackedBarConfig?.yAxisKeys || ['valor']
      let yAxisKeys = yAxisKeysConfig.map(resolveKey)
      if (stackCategoryKey && valueKey && rawData.length > 0) {
        const stackCatResolved = resolveKey(stackCategoryKey)
        const byCategory = new Map<string, Record<string, number>>()
        const seriesNames = new Set<string>()
        rawData.forEach((row: Record<string, unknown>) => {
          const cat = row[xAxisKey] != null ? String(row[xAxisKey]) : ''
          const series = row[stackCatResolved] != null ? String(row[stackCatResolved]) : ''
          const val = Number(row[valueKey]) || 0
          if (!byCategory.has(cat)) byCategory.set(cat, {})
          seriesNames.add(series)
          const rec = byCategory.get(cat)!
          rec[series] = (rec[series] || 0) + val
        })
        chartData = Array.from(byCategory.entries()).map(([cat, seriesValues]) => ({
          [xAxisKey]: cat,
          ...seriesValues,
        }))
        yAxisKeys = Array.from(seriesNames)
      }
      return (
        <StackedBarComponent
          title={componentTitle}
          data={chartData}
          xAxisKey={xAxisKey}
          yAxisKeys={yAxisKeys}
          layout={config.stackedBarConfig?.layout}
          showGrid={config.stackedBarConfig?.showGrid}
          colors={config.stackedBarConfig?.colors}
        />
      )
    }

    case 'scatter-plot':
      return (
        <ScatterPlotComponent
          title={componentTitle}
          data={data || []}
          xAxisKey={config.scatterPlotConfig?.xAxisKey || 'x'}
          yAxisKey={config.scatterPlotConfig?.yAxisKey || 'y'}
          zAxisKey={config.scatterPlotConfig?.zAxisKey}
          groupKey={config.scatterPlotConfig?.groupKey}
          showGrid={config.scatterPlotConfig?.showGrid}
          colors={config.scatterPlotConfig?.colors}
        />
      )

    // =====================================================
    // FASE 3: NICE TO HAVE
    // =====================================================

    case 'heatmap':
      return (
        <HeatmapComponent
          title={componentTitle}
          data={data || []}
          xAxisKey={config.heatmapConfig?.xAxisKey || 'x'}
          yAxisKey={config.heatmapConfig?.yAxisKey || 'y'}
          valueKey={config.heatmapConfig?.valueKey || 'value'}
          colorScale={config.heatmapConfig?.colorScale}
          showValues={config.heatmapConfig?.showValues}
        />
      )

    case 'treemap':
      return (
        <TreemapComponent
          title={componentTitle}
          data={data || []}
          nameKey={config.treemapConfig?.nameKey || 'name'}
          valueKey={config.treemapConfig?.valueKey || 'value'}
          colors={config.treemapConfig?.colors}
        />
      )

    case 'radar-chart':
      return (
        <RadarChartComponent
          title={componentTitle}
          data={data || []}
          subjectKey={config.radarChartConfig?.subjectKey || 'subject'}
          valueKeys={config.radarChartConfig?.valueKeys || ['value']}
          valueKeyLabels={config.customQuery?.config?.fieldAliases}
          colors={config.radarChartConfig?.colors}
          fillOpacity={config.radarChartConfig?.fillOpacity}
        />
      )

    case 'radial-bar':
      return (
        <RadialBarComponent
          title={componentTitle}
          data={data || []}
          nameKey={config.radialBarConfig?.nameKey || 'name'}
          valueKey={config.radialBarConfig?.valueKey || 'value'}
          maxValue={config.radialBarConfig?.maxValue}
          colors={config.radialBarConfig?.colors}
        />
      )

    // =====================================================
    // CKAN: DATOS EXTERNOS
    // =====================================================

    case 'forest-associations-table':
      // Este componente no necesita datos de la base local, consulta directamente CKAN
      return (
        <ForestAssociationsTable
          height={600}
        />
      )

    default:
      return (
        <div className="bg-white border rounded-lg p-6 text-center text-gray-500">
          Tipo de componente desconocido: {component.type}
        </div>
      )
  }
}